import { useFormikContext } from "formik";
import {
  WhmAutocomplete,
  WhmBox,
  WhmFade,
  WhmLoadingButton,
  WhmStack,
  WhmTextField,
} from "@securitize/reactjs-whm";
import { statesUS } from "../Registration/countriesList";
import TranslationKeys from "../../contexts/I18nContext/translationKeys";
import { Translate } from "../Translation/Translate";
import CountryTextField from "../shared/CountryTextField/CountryTextField";
import { useCountries } from "../shared/CountryTextField/CountriesContext";
import { PulsatingSpinner } from "../shared/PulsatingSpinner";
import { ErrorPage } from "../shared/ErrorPage";

export interface CreateEntityFormValues {
  entityName: string;
  entityCountry: { label: string; value: string } | null;
  entityState: { label: string; value: string } | null;
}

export function SubmitButton() {
  const { isValid, dirty, isSubmitting } =
    useFormikContext<CreateEntityFormValues>();
  return (
    <WhmLoadingButton
      variant="contained"
      type="submit"
      disabled={!dirty || !isValid}
      loading={isSubmitting}
      loadingPosition="start"
      sx={{
        ".MuiCircularProgress-root": { display: "flex" },
      }}
    >
      {isSubmitting ? (
        <Translate
          translationKey={TranslationKeys.ADD_ENTITY_BUTTON_IN_PROGRESS}
        />
      ) : (
        <Translate translationKey={TranslationKeys.ADD_ENTITY_BUTTON} />
      )}
    </WhmLoadingButton>
  );
}

export function CreateEntityForm() {
  const formik = useFormikContext<CreateEntityFormValues>();
  const countriesQuery = useCountries();

  if (countriesQuery.error) {
    return (
      <WhmStack sx={{ height: "100%" }} spacing={6} alignItems="center">
        <ErrorPage
          errorCode={countriesQuery.error.correlationId}
          onRefresh={countriesQuery.refetch}
        />
      </WhmStack>
    );
  }

  if (countriesQuery.isLoading) {
    return (
      <WhmBox sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <PulsatingSpinner />
      </WhmBox>
    );
  }

  return (
    <WhmStack spacing={4} sx={{ width: "100%" }}>
      <WhmTextField
        label={
          <Translate
            translationKey={TranslationKeys.REGISTRATION_ENTITY_NAME}
          />
        }
        fullWidth
        name="entityName"
        value={formik.values.entityName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.entityName && Boolean(formik.errors.entityName)}
        helperText={formik.touched.entityName && formik.errors.entityName}
      />
      <CountryTextField
        name="entityCountry"
        value={formik.values.entityCountry?.value ?? ""}
        label={
          <Translate
            translationKey={TranslationKeys.REGISTRATION_ENTITY_COUNTRY}
          />
        }
        fieldError={
          formik.touched.entityCountry && Boolean(formik.errors.entityCountry)
        }
        helperText={
          formik.touched.entityCountry && formik.errors.entityCountry
            ? String(formik.errors.entityCountry)
            : undefined
        }
        onChange={(_e, v) => {
          void formik.setFieldValue("entityCountry", v).then(() => {
            void formik.setFieldTouched("entityCountry", true, true);
          });
          if (v?.value !== "US") {
            void formik.setFieldValue("entityState", null);
          }
        }}
      />
      <WhmFade
        in={formik.values.entityCountry?.value === "US"}
        mountOnEnter
        unmountOnExit
      >
        <WhmAutocomplete
          options={statesUS}
          onChange={(_e, v) => {
            void formik.setFieldValue("entityState", v).then(() => {
              void formik.setFieldTouched("entityState", true, true);
            });
          }}
          value={formik.values.entityState}
          fullWidth
          renderInput={(props) => (
            <WhmTextField
              {...props}
              label={
                <Translate
                  translationKey={TranslationKeys.REGISTRATION_ENTITY_STATE}
                />
              }
              name="entityState"
              error={
                formik.touched.entityState && Boolean(formik.errors.entityState)
              }
              helperText={
                formik.touched.entityState && formik.errors.entityState
              }
            />
          )}
        />
      </WhmFade>
    </WhmStack>
  );
}
